'use client';

import React from 'react';
import { Users, TrendingUp, Eye, BarChart3, Heart } from 'lucide-react';
import { PageData } from './PageAnalyticsContent';
import Icon from '@/components/ui/AppIcon';


interface Props {
  page: PageData;
}

export default function PageKPICards({ page }: Props) {
  const cards = [
    {
      id: 'pkpi-followers',
      label: 'Total Followers',
      value: page.followers >= 1000 ? `${(page.followers / 1000).toFixed(1)}K` : page.followers.toString(),
      delta: `${page.followerGrowth >= 0 ? '+' : ''}${page.followerGrowth.toLocaleString()} this week`,
      positive: page.followerGrowth >= 0,
      icon: Users,
      color: 'bg-primary/10 text-primary',
    },
    {
      id: 'pkpi-growth',
      label: 'Follower Growth Rate',
      value: `${page.followerGrowthPct >= 0 ? '+' : ''}${page.followerGrowthPct.toFixed(2)}%`,
      delta: page.followerGrowthPct >= 0 ? 'Growing this week' : 'Declining this week',
      positive: page.followerGrowthPct >= 0,
      icon: TrendingUp,
      color: page.followerGrowthPct >= 0 ? 'bg-positive/10 text-positive' : 'bg-negative/10 text-negative',
    },
    {
      id: 'pkpi-reach',
      label: 'Reach (7 days)',
      value: page.reach7d >= 1000 ? `${(page.reach7d / 1000).toFixed(0)}K` : page.reach7d.toString(),
      delta: 'Unique accounts reached',
      positive: null,
      icon: Eye,
      color: 'bg-info/10 text-info',
    },
    {
      id: 'pkpi-impressions',
      label: 'Impressions (7 days)',
      value: page.impressions7d >= 1000 ? `${(page.impressions7d / 1000).toFixed(0)}K` : page.impressions7d.toString(),
      delta: `Freq. ${(page.impressions7d / page.reach7d).toFixed(1)}× avg`,
      positive: null,
      icon: BarChart3,
      color: 'bg-muted text-muted-foreground',
    },
    {
      id: 'pkpi-engagement',
      label: 'Engagement Rate',
      value: `${page.engagementRate.toFixed(1)}%`,
      delta: page.engagementRate >= 3.0 ? 'Above 3% industry benchmark' : 'Below 3% benchmark',
      positive: page.engagementRate >= 3.0,
      icon: Heart,
      color: page.engagementRate >= 3.0 ? 'bg-positive/10 text-positive' : 'bg-warning/10 text-warning',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-5 2xl:grid-cols-5 gap-4">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <div key={card.id} className="card-base p-5 card-hover">
            <div className="flex items-start justify-between mb-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground leading-tight">{card.label}</p>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${card.color}`}>
                <Icon size={14} />
              </div>
            </div>
            <p className="text-2xl font-bold text-foreground tabular-nums">{card.value}</p>
            {card.delta && (
              <p className={`text-xs mt-1.5 font-medium ${
                card.positive === true ? 'text-positive' :
                card.positive === false ? 'text-negative': 'text-muted-foreground'
              }`}>
                {card.delta}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}